import React, { useContext } from 'react';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/solid';

const parsePrice = (p) => {
  if (!p) return 0;
  const num = String(p).replace(/[^0-9.]/g, '');
  return parseFloat(num) || 0;
};

const formatCurrency = (n) => {
  return `PHP ${n.toFixed(2)}`;
};

const Cart = () => {
  const { items = [], removeItem, clear, updateItemQty, count } = useContext(CartContext);
  const { isLoggedIn, openLoginModal } = useContext(AuthContext);
  const navigate = useNavigate();

  const subtotal = items.reduce((s, it) => s + (parsePrice(it.price) * (it.qty || 0)), 0);

  const handleCheckout = () => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    // if you have a checkout route, navigate there; placeholder for now
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h2 className="text-4xl sm:text-5xl md:text-6xl font-bebas mb-4 sm:mb-6">Your Cart</h2>

        {items.length === 0 ? (
            <div className="bg-white p-6 sm:p-8 rounded shadow">
            <p className="text-gray-600 mb-4 text-sm sm:text-base">Your tray is empty.</p>
            <Link to="/menu" className="inline-block bg-black text-white px-4 py-2 rounded text-sm sm:text-base hover:bg-gray-800 transition-colors">Browse Menu</Link>
          </div>
        ) : (
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="lg:col-span-2">
              <div className="space-y-3 sm:space-y-4">
                {items.map((it) => (
                  <div key={it.id} className="flex items-start sm:items-center gap-3 sm:gap-4 bg-[#fafafa] p-3 sm:p-4 rounded shadow-sm">
                    {/* fixed-size image box that will not shrink; keeps consistent alignment */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0">
                      {it.img ? (
                        <img src={it.img} alt={it.title} className="w-full h-full object-cover object-center" />
                      ) : (
                        <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="32" cy="32" r="30" stroke="#e5e7eb" strokeWidth="2" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-montserrat font-bold text-sm sm:text-base truncate">{it.title}</h3>
                          <div className="text-xs sm:text-sm text-gray-600">{it.price}</div>
                        </div>
                        <div className="flex-shrink-0">
                          <button
                            onClick={() => removeItem(it.id)}
                            aria-label={`Remove ${it.title}`}
                            title="Remove"
                            className="text-red-500 hover:opacity-80 p-1 rounded active:scale-95 transition-transform"
                          >
                            <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden />
                          </button>
                        </div>
                      </div>

                      <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="flex items-center bg-white border rounded-full px-1 sm:px-2 py-1 w-fit">
                          <button
                            onClick={() => updateItemQty(it.id, (it.qty || 1) - 1)}
                            disabled={(it.qty || 1) <= 1}
                            className={`px-2 sm:px-3 py-1 text-gray-700 rounded-full text-base sm:text-lg ${((it.qty || 1) <= 1) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 active:scale-95'}`}
                            aria-label={`Decrease quantity for ${it.title}`}
                          >
                            −
                          </button>

                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={it.qty || 1}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              if (Number.isNaN(v)) {
                                updateItemQty(it.id, 1);
                              } else {
                                updateItemQty(it.id, Math.max(1, v));
                              }
                            }}
                            onBlur={(e) => {
                              const v = parseInt(e.target.value, 10);
                              updateItemQty(it.id, Math.max(1, Number.isNaN(v) ? 1 : v));
                            }}
                            className="no-spinner w-12 sm:w-16 text-center font-medium text-sm sm:text-base text-gray-800 border-none outline-none"
                            aria-label={`Quantity for ${it.title}`}
                          />

                          <button
                            onClick={() => updateItemQty(it.id, (it.qty || 1) + 1)}
                            className="px-2 sm:px-3 py-1 text-gray-700 hover:bg-gray-100 rounded-full active:scale-95 text-base sm:text-lg"
                            aria-label={`Increase quantity for ${it.title}`}
                          >
                            +
                          </button>
                        </div>

                        <div className="text-xs sm:text-sm text-gray-700">Subtotal: <span className="font-bold">{formatCurrency(parsePrice(it.price) * (it.qty || 0))}</span></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="bg-white p-4 sm:p-6 rounded shadow lg:sticky lg:top-4 lg:self-start">
              <div className="mb-4 sm:mb-6">
                <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2"><span>Items</span><span>{count}</span></div>
                <div className="flex justify-between font-bold text-base sm:text-lg"> <span>Total</span> <span>{formatCurrency(subtotal)}</span></div>
              </div>

              <div className="space-y-2">
                <button onClick={handleCheckout} className="w-full bg-black text-white py-2.5 sm:py-2 rounded text-sm sm:text-base hover:bg-gray-800 transition-colors active:scale-[0.98]">Checkout</button>
                <button onClick={() => clear()} className="w-full border border-gray-300 py-2.5 sm:py-2 rounded text-sm sm:text-base hover:bg-gray-50 transition-colors active:scale-[0.98]">Clear Tray</button>
                <Link to="/menu" className="w-full block text-center text-xs sm:text-sm text-gray-600 hover:text-gray-800 py-2">Continue shopping</Link>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
